// Messenger Calling context — owns the WebRTC RTCPeerConnection
// lifecycle for inbound Meta Messenger calls (and later, outbound B2C).
//
// Mirrors the design of TwilioVoiceContext but built on raw WebRTC:
//   - Subscribes to messengerCallEvent for incoming-ring push
//   - Maintains an incomingQueue of ringing calls
//   - Builds an RTCPeerConnection on Accept, creates SDP offer, sends
//     it through claimAndAnswerMessengerCall mutation, applies Meta's
//     sdp_response as remoteDescription → media flows
//   - Handles mid-call SDP renegotiation via media_update events
//   - Exposes a single activeCall + queue for the toast & widget
//
// Recording (MediaRecorder upload to S3) will be added in Phase 2.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useSubscription } from "@apollo/client";
import { useSnackbar } from "notistack";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import MESSENGER_CALL_EVENT_SUBSCRIPTION from "src/graphql/subscription/messengerCall/messengerCallEvent";
import CLAIM_AND_ANSWER_MESSENGER_CALL from "src/graphql/mutation/messengerCall/claimAndAnswer";
import REJECT_MESSENGER_CALL from "src/graphql/mutation/messengerCall/rejectCall";
import END_MESSENGER_CALL from "src/graphql/mutation/messengerCall/endCall";
import UPLOAD_MESSENGER_CALL_RECORDING from "src/graphql/mutation/messengerCall/uploadRecording";
import INITIATE_MESSENGER_CALL from "src/graphql/mutation/messengerCall/initiateCall";

export interface MessengerCallEntry {
  callId: string;
  pageId: string;
  psid: string;
  inboxId: string | null;
  customerName: string | null;
  customerPicture: string | null;
  direction: string;
  status: string;
}

type CallState = "idle" | "incoming" | "calling" | "in-call";

interface MessengerCallContextType {
  callState: CallState;
  incomingQueue: MessengerCallEntry[];
  activeCall: MessengerCallEntry | null;
  remoteStream: MediaStream | null;
  durationSeconds: number;
  formattedDuration: string;
  isMuted: boolean;
  isRecording: boolean;
  isUploadingRecording: boolean;
  hasIncoming: boolean;
  isOnCall: boolean;
  acceptCall: (callId: string) => Promise<void>;
  rejectCall: (callId: string) => Promise<void>;
  hangup: () => Promise<void>;
  toggleMute: () => void;
  initiateOutboundCall: (params: {
    inboxId: string;
    customerName: string | null;
    customerPicture: string | null;
  }) => Promise<{ success: boolean; error?: string }>;
}

const MessengerCallContext = createContext<
  MessengerCallContextType | undefined
>(undefined);

// Free public STUN; falls back to direct connectivity. If your customers
// hit NAT issues we'll swap this for a TURN credential later.
const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export const MessengerCallProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mainSuperAdminId, agentId: myAgentId } = useActiveUserDetail();

  const [callState, setCallState] = useState<CallState>("idle");
  const [incomingQueue, setIncomingQueue] = useState<MessengerCallEntry[]>([]);
  const [activeCall, setActiveCall] = useState<MessengerCallEntry | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploadingRecording, setIsUploadingRecording] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const callStartedAtRef = useRef<number | null>(null);
  const activeCallIdRef = useRef<string | null>(null);

  // MediaRecorder + Web Audio plumbing for call recording. The mixer
  // combines the agent's mic and the customer's remote stream into a
  // single MediaStream that the recorder consumes.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const recordingDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);

  const [claimAndAnswer] = useMutation(CLAIM_AND_ANSWER_MESSENGER_CALL);
  const [rejectMut] = useMutation(REJECT_MESSENGER_CALL);
  const [endMut] = useMutation(END_MESSENGER_CALL);
  const [uploadRecording] = useMutation(UPLOAD_MESSENGER_CALL_RECORDING);
  const [initiateCallMutation] = useMutation(INITIATE_MESSENGER_CALL);

  // ──────────────────────────────────────────────────────────────────
  // Recording (MediaRecorder + Web Audio mixer)
  // ──────────────────────────────────────────────────────────────────

  const startRecording = useCallback(
    (localStream: MediaStream, remoteMediaStream: MediaStream) => {
      try {
        const Ctor: typeof AudioContext | undefined =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return;
        const ctx = new Ctor();
        const dest = ctx.createMediaStreamDestination();
        ctx.createMediaStreamSource(localStream).connect(dest);
        ctx.createMediaStreamSource(remoteMediaStream).connect(dest);
        audioCtxRef.current = ctx;
        recordingDestRef.current = dest;

        const mr = new MediaRecorder(dest.stream, {
          mimeType: "audio/webm",
        });
        recorderChunksRef.current = [];
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            recorderChunksRef.current.push(e.data);
          }
        };
        mr.start(500);
        recorderRef.current = mr;
        setIsRecording(true);
      } catch (err) {
        // Recording failures are non-fatal — the call continues.
        // eslint-disable-next-line no-console
        console.error("[MessengerCall] startRecording failed:", err);
      }
    },
    [],
  );

  // Stop the recorder, hand back the accumulated Blob (or null if
  // recording wasn't running or produced nothing).
  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    const mr = recorderRef.current;
    if (!mr) return null;
    return new Promise<Blob | null>((resolve) => {
      try {
        mr.onstop = () => {
          const chunks = recorderChunksRef.current;
          recorderChunksRef.current = [];
          if (chunks.length === 0) {
            resolve(null);
            return;
          }
          resolve(new Blob(chunks, { type: "audio/webm" }));
        };
        if (mr.state !== "inactive") mr.stop();
        else resolve(null);
      } catch {
        resolve(null);
      } finally {
        recorderRef.current = null;
        if (audioCtxRef.current) {
          audioCtxRef.current.close().catch(() => {});
          audioCtxRef.current = null;
        }
        recordingDestRef.current = null;
        setIsRecording(false);
      }
    });
  }, []);

  // Stop recording AND upload the resulting blob, all in one go. Called
  // from hangup / teardown so the upload happens regardless of who
  // ended the call (agent, customer, or Meta timeout).
  const finalizeRecording = useCallback(
    async (callId: string) => {
      const blob = await stopRecording();
      if (!blob || blob.size === 0) return;
      try {
        setIsUploadingRecording(true);
        const file = new File([blob], `${callId}.webm`, {
          type: "audio/webm",
        });
        await uploadRecording({ variables: { callId, file } });
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Recording upload failed", {
          variant: "warning",
        });
      } finally {
        setIsUploadingRecording(false);
      }
    },
    [stopRecording, uploadRecording, enqueueSnackbar],
  );

  // ──────────────────────────────────────────────────────────────────
  // RTCPeerConnection lifecycle
  // ──────────────────────────────────────────────────────────────────

  const teardown = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    // Stop + upload the recording before we tear down the local stream
    // (otherwise the mixer source nodes get disconnected). Fire-and-
    // forget — upload runs in the background, surfaced via the
    // isUploadingRecording state for the widget.
    const endingCallId = activeCallIdRef.current;
    if (endingCallId && recorderRef.current) {
      finalizeRecording(endingCallId).catch(() => {});
    }
    callStartedAtRef.current = null;
    activeCallIdRef.current = null;
    if (localStreamRef.current) {
      try {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch {
        /* noop */
      }
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch {
        /* noop */
      }
      pcRef.current = null;
    }
    setRemoteStream(null);
    setActiveCall(null);
    setDurationSeconds(0);
    setIsMuted(false);
    setCallState((prev) => (prev === "incoming" ? "incoming" : "idle"));
  }, [finalizeRecording]);

  const acceptCall = useCallback(
    async (callId: string) => {
      if (activeCallIdRef.current) {
        enqueueSnackbar("Already on a call.", { variant: "warning" });
        return;
      }
      const entry = incomingQueue.find((e) => e.callId === callId);
      if (!entry) return;

      // Pull the entry out of the visible queue immediately so the
      // ringing card disappears the instant the agent clicks Accept.
      // If the accept ultimately fails, teardown() clears the active
      // widget and the agent sees the error snackbar — no stale UI.
      setIncomingQueue((prev) => prev.filter((e) => e.callId !== callId));

      // Surface the active-call widget right away ("Connecting…") so
      // the user doesn't see an empty screen during the ~1-2s window
      // we spend on mic capture + ICE gathering + Meta accept. If
      // anything below throws, teardown() rolls this back cleanly.
      activeCallIdRef.current = callId;
      setActiveCall(entry);
      setCallState("calling");

      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        localStreamRef.current = localStream;

        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;

        localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));

        pc.ontrack = (e) => {
          if (e.streams && e.streams[0]) {
            const stream = e.streams[0];
            setRemoteStream(stream);
            // Kick off recording once we have both directions of audio.
            // ontrack fires before connectionState=connected, so this is
            // the earliest the mixer can build a valid combined stream.
            if (localStreamRef.current && !recorderRef.current) {
              startRecording(localStreamRef.current, stream);
            }
          }
        };
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === "connected") {
            if (!callStartedAtRef.current) {
              callStartedAtRef.current = Date.now();
              durationIntervalRef.current = setInterval(() => {
                if (callStartedAtRef.current) {
                  setDurationSeconds(
                    Math.floor((Date.now() - callStartedAtRef.current) / 1000),
                  );
                }
              }, 1000);
            }
            setCallState("in-call");
          } else if (
            pc.connectionState === "failed" ||
            pc.connectionState === "disconnected" ||
            pc.connectionState === "closed"
          ) {
            teardown();
          }
        };

        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
        });
        await pc.setLocalDescription(offer);

        // Wait for ICE candidates to gather (briefly) so the SDP we
        // send to Meta is complete. Avoids reconnect loops.
        await new Promise<void>((resolve) => {
          if (pc.iceGatheringState === "complete") {
            resolve();
            return;
          }
          const onChange = () => {
            if (pc.iceGatheringState === "complete") {
              pc.removeEventListener("icegatheringstatechange", onChange);
              resolve();
            }
          };
          pc.addEventListener("icegatheringstatechange", onChange);
          // Hard cap so we don't stall forever on a flaky network.
          setTimeout(() => {
            pc.removeEventListener("icegatheringstatechange", onChange);
            resolve();
          }, 1500);
        });

        const finalOffer = pc.localDescription?.sdp || offer.sdp || "";

        const { data } = await claimAndAnswer({
          variables: { callId, sdpOffer: finalOffer },
        });
        const out = data?.claimAndAnswerMessengerCall;
        if (!out?.success) {
          enqueueSnackbar(out?.error || "Failed to accept call", {
            variant: "error",
          });
          teardown();
          return;
        }
        if (typeof out.sdpResponse === "string" && out.sdpResponse.length > 0) {
          await pc.setRemoteDescription({
            type: "answer",
            sdp: out.sdpResponse,
          });
        }
        if (
          typeof out.sdpRenegotiation === "string" &&
          out.sdpRenegotiation.length > 0
        ) {
          // Some flows return a renegotiation offer in the same response.
          // Apply it after the answer, generate a new local answer, and
          // re-set local description.
          await pc.setRemoteDescription({
            type: "offer",
            sdp: out.sdpRenegotiation,
          });
          const reAnswer = await pc.createAnswer();
          await pc.setLocalDescription(reAnswer);
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Could not start call", {
          variant: "error",
        });
        teardown();
      }
    },
    [claimAndAnswer, enqueueSnackbar, incomingQueue, teardown],
  );

  // Outbound (B2C) — agent clicks Call from a Messenger chat header.
  // Builds the same WebRTC peer connection acceptCall uses, then sends
  // the SDP offer to initiateMessengerCall. The active widget renders
  // identically to inbound from there on.
  const initiateOutboundCall = useCallback(
    async (params: {
      inboxId: string;
      customerName: string | null;
      customerPicture: string | null;
    }) => {
      if (activeCallIdRef.current) {
        enqueueSnackbar("Already on a call.", { variant: "warning" });
        return { success: false, error: "Already on a call" };
      }

      // Surface the active widget immediately with the customer name so
      // there's no empty gap while we set up mic + ICE + Meta initiate.
      const placeholderEntry: MessengerCallEntry = {
        callId: `pending:${params.inboxId}`,
        pageId: "",
        psid: "",
        inboxId: params.inboxId,
        customerName: params.customerName,
        customerPicture: params.customerPicture,
        direction: "business_initiated",
        status: "calling",
      };
      activeCallIdRef.current = placeholderEntry.callId;
      setActiveCall(placeholderEntry);
      setCallState("calling");

      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        localStreamRef.current = localStream;

        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;

        localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));

        pc.ontrack = (e) => {
          if (e.streams && e.streams[0]) {
            const stream = e.streams[0];
            setRemoteStream(stream);
            if (localStreamRef.current && !recorderRef.current) {
              startRecording(localStreamRef.current, stream);
            }
          }
        };
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === "connected") {
            if (!callStartedAtRef.current) {
              callStartedAtRef.current = Date.now();
              durationIntervalRef.current = setInterval(() => {
                if (callStartedAtRef.current) {
                  setDurationSeconds(
                    Math.floor((Date.now() - callStartedAtRef.current) / 1000),
                  );
                }
              }, 1000);
            }
            setCallState("in-call");
          } else if (
            pc.connectionState === "failed" ||
            pc.connectionState === "disconnected" ||
            pc.connectionState === "closed"
          ) {
            teardown();
          }
        };

        const offer = await pc.createOffer({ offerToReceiveAudio: true });
        await pc.setLocalDescription(offer);

        await new Promise<void>((resolve) => {
          if (pc.iceGatheringState === "complete") {
            resolve();
            return;
          }
          const onChange = () => {
            if (pc.iceGatheringState === "complete") {
              pc.removeEventListener("icegatheringstatechange", onChange);
              resolve();
            }
          };
          pc.addEventListener("icegatheringstatechange", onChange);
          setTimeout(() => {
            pc.removeEventListener("icegatheringstatechange", onChange);
            resolve();
          }, 1500);
        });

        const finalOffer = pc.localDescription?.sdp || offer.sdp || "";

        const { data } = await initiateCallMutation({
          variables: { inboxId: params.inboxId, sdpOffer: finalOffer },
        });
        const out = data?.initiateMessengerCall;
        if (!out?.success || !out?.call?.callId) {
          enqueueSnackbar(out?.error || "Failed to place call", {
            variant: "error",
          });
          teardown();
          return { success: false, error: out?.error };
        }

        // Swap the placeholder callId for the real one Meta issued.
        activeCallIdRef.current = out.call.callId;
        setActiveCall({
          callId: out.call.callId,
          pageId: out.call.pageId,
          psid: out.call.psid,
          inboxId: out.call.inboxId,
          customerName: out.call.customerName,
          customerPicture: out.call.customerPicture,
          direction: out.call.direction,
          status: out.call.status,
        });

        if (typeof out.sdpResponse === "string" && out.sdpResponse.length > 0) {
          await pc.setRemoteDescription({
            type: "answer",
            sdp: out.sdpResponse,
          });
        }
        if (
          typeof out.sdpRenegotiation === "string" &&
          out.sdpRenegotiation.length > 0
        ) {
          await pc.setRemoteDescription({
            type: "offer",
            sdp: out.sdpRenegotiation,
          });
          const reAnswer = await pc.createAnswer();
          await pc.setLocalDescription(reAnswer);
        }

        return { success: true };
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Could not start call", {
          variant: "error",
        });
        teardown();
        return { success: false, error: err?.message };
      }
    },
    [
      activeCallIdRef,
      enqueueSnackbar,
      initiateCallMutation,
      startRecording,
      teardown,
    ],
  );

  const rejectCall = useCallback(
    async (callId: string) => {
      setIncomingQueue((prev) => prev.filter((e) => e.callId !== callId));
      try {
        await rejectMut({ variables: { callId } });
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Reject failed", { variant: "error" });
      }
    },
    [rejectMut, enqueueSnackbar],
  );

  const hangup = useCallback(async () => {
    const callId = activeCallIdRef.current;
    if (!callId) return;
    try {
      await endMut({ variables: { callId } });
    } catch (err: any) {
      // Server-side hangup might fail; we still tear down locally.
      enqueueSnackbar(err?.message || "Hangup failed", { variant: "warning" });
    }
    teardown();
  }, [endMut, enqueueSnackbar, teardown]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const tracks = stream.getAudioTracks();
    const newEnabled = !(tracks[0]?.enabled ?? true);
    tracks.forEach((t) => {
      t.enabled = newEnabled;
    });
    setIsMuted(!newEnabled);
  }, []);

  // ──────────────────────────────────────────────────────────────────
  // Subscription — incoming-ring + lifecycle pushes from backend
  // ──────────────────────────────────────────────────────────────────

  useSubscription(MESSENGER_CALL_EVENT_SUBSCRIPTION, {
    skip: !mainSuperAdminId,
    variables: { mainSuperAdminId },
    onData: ({ data }) => {
      const evt = (data?.data as any)?.messengerCallEvent;
      if (!evt) return;
      const { type, call } = evt;

      if (type === "ringing" && call) {
        // Defensive: if this callId is ALREADY our active call (e.g.
        // outbound where backend forgot to skip the connect echo),
        // never queue it as an incoming ring.
        if (activeCallIdRef.current === call.callId) {
          return;
        }
        // Sticky-route filter: when the call already has an assigned
        // agent (customer was previously claimed), only show the modal
        // on THAT agent's screen. Unassigned calls (agentId == null)
        // fan out to everyone for the claim-race.
        if (
          call.agentId &&
          myAgentId &&
          Number(call.agentId) !== Number(myAgentId)
        ) {
          return;
        }
        // Add to queue (de-dupe against other queued entries).
        setIncomingQueue((prev) => {
          if (prev.some((e) => e.callId === call.callId)) return prev;
          return [
            ...prev,
            {
              callId: call.callId,
              pageId: call.pageId,
              psid: call.psid,
              inboxId: call.inboxId,
              customerName: call.customerName,
              customerPicture: call.customerPicture,
              direction: call.direction,
              status: call.status,
            },
          ];
        });
        setCallState((prev) =>
          prev === "idle" || prev === "incoming" ? "incoming" : prev,
        );
      } else if (type === "claim_updated" && call) {
        // The pending-chats distribution path just dropped this
        // customer into someone's inbox.
        //   - If it's me  → update my queue entry's inboxId + agentId
        //     so the Accept button enables.
        //   - If it's NOT me → drop the call from my queue; the
        //     claimer now owns it.
        const claimerId = call.agentId ? Number(call.agentId) : null;
        const me = myAgentId ? Number(myAgentId) : null;
        if (claimerId && me && claimerId === me) {
          setIncomingQueue((prev) =>
            prev.map((e) =>
              e.callId === call.callId
                ? {
                    ...e,
                    inboxId: call.inboxId,
                    customerName: call.customerName || e.customerName,
                    customerPicture: call.customerPicture || e.customerPicture,
                  }
                : e,
            ),
          );
        } else if (claimerId) {
          setIncomingQueue((prev) =>
            prev.filter((e) => e.callId !== call.callId),
          );
        }
      } else if (
        type === "accepted" ||
        type === "rejected" ||
        type === "terminated"
      ) {
        // Someone (possibly another agent) handled the call. Drop it
        // from this agent's queue. If it was OUR active call ending,
        // tear down our peer connection.
        if (call?.callId) {
          setIncomingQueue((prev) =>
            prev.filter((e) => e.callId !== call.callId),
          );
          if (
            activeCallIdRef.current &&
            activeCallIdRef.current === call.callId &&
            (type === "terminated" || type === "rejected")
          ) {
            teardown();
          }
        }
      } else if (type === "media_update") {
        // SDP renegotiation from Meta during the call.
        const pc = pcRef.current;
        const sdp = evt?.session?.sdp_renegotiation?.sdp;
        if (pc && sdp && activeCallIdRef.current === evt.callId) {
          (async () => {
            try {
              await pc.setRemoteDescription({ type: "offer", sdp });
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
            } catch (err: any) {
              // Renegotiation failure is non-fatal; logged for debug.
              // eslint-disable-next-line no-console
              console.error("Messenger media_update apply failed:", err);
            }
          })();
        }
      }
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error("messengerCallEvent subscription error:", err);
    },
  });

  // ──────────────────────────────────────────────────────────────────

  const value = useMemo<MessengerCallContextType>(
    () => ({
      callState,
      incomingQueue,
      activeCall,
      remoteStream,
      durationSeconds,
      formattedDuration: formatDuration(durationSeconds),
      isMuted,
      isRecording,
      isUploadingRecording,
      hasIncoming: incomingQueue.length > 0,
      isOnCall: callState === "calling" || callState === "in-call",
      acceptCall,
      rejectCall,
      hangup,
      toggleMute,
      initiateOutboundCall,
    }),
    [
      callState,
      incomingQueue,
      activeCall,
      remoteStream,
      durationSeconds,
      isMuted,
      isRecording,
      isUploadingRecording,
      acceptCall,
      rejectCall,
      hangup,
      toggleMute,
      initiateOutboundCall,
    ],
  );

  // Cleanup on unmount (e.g. logout).
  useEffect(() => {
    return () => {
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MessengerCallContext.Provider value={value}>
      {children}
    </MessengerCallContext.Provider>
  );
};

export function useMessengerCall(): MessengerCallContextType {
  const ctx = useContext(MessengerCallContext);
  if (!ctx) {
    throw new Error(
      "useMessengerCall must be used within MessengerCallProvider",
    );
  }
  return ctx;
}

export default MessengerCallContext;
